import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { SMART_LINK_STATS_DAYS_OPTIONS } from 'fym-shared/src/smartlinks/stats';
import { TypedMemo } from '../../common/types';
import { Segmented } from '../../music24/components/primitives/Segmented';
import { SMLUpgradeFeature } from '../../music24/config/music24PlanCapabilities';
import { useUpgradePrompt } from '../../music24/hooks/useUpgradePrompt';
import { SmartLinkStatsDays } from '../../../redux/api/fymAPI.types';

const messages = defineMessages({
  days: { id: 'app.sml.stats.days.option', defaultMessage: '{days}d' },
  label: { id: 'app.sml.stats.days.label', defaultMessage: 'Period' },
  locked: {
    id: 'app.sml.stats.days.locked',
    defaultMessage: 'See {days} days of link stats on a bigger plan',
  },
});

interface SmartLinkStatsDaysPickerProps {
  days: SmartLinkStatsDays;
  /** The history of the plan (`historyDaysLimit`). A longer period has a lock. */
  historyDaysLimit: number;
  onChange: (days: SmartLinkStatsDays) => void;
}

/**
 * The period of the SmartLink stats: 7, 14, 28, 90, 180 or 365 days. A period longer than the plan
 * history has a lock and opens the upgrade prompt (history), and the server cuts it too.
 */
const SmartLinkStatsDaysPickerComponent: React.FC<SmartLinkStatsDaysPickerProps> = ({
  days,
  historyDaysLimit,
  onChange,
}) => {
  const intl = useIntl();
  const openUpgradePrompt = useUpgradePrompt();
  const options = useMemo(
    () =>
      SMART_LINK_STATS_DAYS_OPTIONS.map((option) => {
        const isLocked = option > historyDaysLimit;
        return {
          value: String(option),
          label: isLocked ? (
            <span
              title={intl.formatMessage(messages.locked, { days: option })}
              data-testid={`sml-stats-days-lock-${option}`}
            >
              <FontAwesomeIcon icon={faLock} /> {intl.formatMessage(messages.days, { days: option })}
            </span>
          ) : (
            intl.formatMessage(messages.days, { days: option })
          ),
        };
      }),
    [historyDaysLimit, intl]
  );
  const pick = useCallback(
    (value: string) => {
      const next = Number(value) as SmartLinkStatsDays;
      if (next > historyDaysLimit) {
        openUpgradePrompt(SMLUpgradeFeature.history);
        return;
      }
      onChange(next);
    },
    [historyDaysLimit, onChange, openUpgradePrompt]
  );

  return (
    <Segmented options={options} value={String(days)} onChange={pick} ariaLabel={intl.formatMessage(messages.label)} />
  );
};

export const SmartLinkStatsDaysPicker = TypedMemo(SmartLinkStatsDaysPickerComponent);
